import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  RocketLaunch,
} from '@mui/icons-material';
import { useActivationStatus } from '../hooks/useActivationStatus';

const STEP_DEFS = [
  {
    key: 'hasClient',
    title: 'Create a client workspace',
    detail: 'Add your first client to organize platforms, media, and posts.',
    actionLabel: 'Go to Clients',
    to: '/clients',
  },
  {
    key: 'hasPlatform',
    title: 'Connect a publishing platform',
    detail:
      'Link Facebook, Instagram, LinkedIn, or another platform for that client.',
    actionLabel: 'Connect platforms',
    to: (clientId) =>
      clientId ? `/clients?client_id=${clientId}` : '/clients',
  },
  {
    key: 'hasPost',
    title: 'Create or schedule your first post',
    detail:
      'Draft a post or add one to the schedule. Scheduled posts count toward activation.',
    actionLabel: 'Create a post',
    to: '/posts',
    secondaryLabel: 'Open Scheduling',
    secondaryTo: '/scheduling',
  },
];

function StepRow({ done, title, detail, actionLabel, to, secondaryLabel, secondaryTo }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        py: 1.5,
        borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ pt: 0.25, color: done ? '#2ea043' : '#484f58' }}>
        {done ? <CheckCircle fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: done ? '#8b949e' : '#f0f6fc',
            fontWeight: 600,
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {title}
        </Typography>
        {!done && (
          <Typography variant="body2" sx={{ color: '#8b949e', mt: 0.25, mb: 1 }}>
            {detail}
          </Typography>
        )}
        {!done && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              component={RouterLink}
              to={to}
              size="small"
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {actionLabel}
            </Button>
            {secondaryTo && (
              <Button
                component={RouterLink}
                to={secondaryTo}
                size="small"
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                {secondaryLabel}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Dashboard checklist until minimum activation (client → platform → post).
 */
export default function ActivationBanner() {
  const { loading, steps, isComplete, clientId } = useActivationStatus();

  if (loading || isComplete) {
    return null;
  }

  const completedCount = [
    steps.hasClient,
    steps.hasPlatform,
    steps.hasPost,
  ].filter(Boolean).length;
  const progress = (completedCount / 3) * 100;

  return (
    <Card
      sx={{
        mb: 4,
        background: 'linear-gradient(135deg, #161b22 0%, #1c2128 100%)',
        border: '1px solid rgba(88, 166, 255, 0.35)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(88, 166, 255, 0.08)',
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(88, 166, 255, 0.12)',
              color: '#58a6ff',
              flexShrink: 0,
            }}
          >
            <RocketLaunch />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 700 }}>
              Get started with Nexus
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e', mt: 0.5 }}>
              Complete these steps to go from signup to your first scheduled post.
              No separate wizard — use the pages you already have.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(48, 54, 61, 0.8)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#58a6ff' },
                }}
              />
              <Typography variant="caption" sx={{ color: '#8b949e', whiteSpace: 'nowrap' }}>
                {completedCount} of 3 complete
              </Typography>
            </Box>
          </Box>
        </Box>

        {STEP_DEFS.map((step) => {
          const done = steps[step.key];
          let to = step.to;
          if (typeof to === 'function') {
            to = to(clientId);
          }
          return (
            <StepRow
              key={step.key}
              done={done}
              title={step.title}
              detail={step.detail}
              actionLabel={step.actionLabel}
              to={to}
              secondaryLabel={step.secondaryLabel}
              secondaryTo={step.secondaryTo}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}
